import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Loader2, Shield, AlertCircle, CheckCircle2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { format } from "date-fns";

// Schema for validating the token input
const tokenSchema = z.object({
  token: z.string().min(6, "Must be at least 6 digits").max(8, "Must be at most 8 digits"),
});

type TokenFormValues = z.infer<typeof tokenSchema>;

export function MFAManagement({ onSetupMFA }: { onSetupMFA?: () => void }) {
  const [showDisableForm, setShowDisableForm] = useState(false);
  const [showNewBackupCodes, setShowNewBackupCodes] = useState(false);
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Form for token verification
  const disableForm = useForm<TokenFormValues>({
    resolver: zodResolver(tokenSchema),
    defaultValues: {
      token: "",
    },
    mode: "onSubmit",
  });

  // Form for generating new backup codes
  const backupCodesForm = useForm<TokenFormValues>({
    resolver: zodResolver(tokenSchema),
    defaultValues: {
      token: "",
    },
    mode: "onSubmit",
  });

  // Query to get MFA status
  const { data: mfaStatus, isLoading } = useQuery({
    queryKey: ["/api/mfa/status"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/mfa/status");
      if (!res.ok) throw new Error("Failed to get MFA status");
      return res.json();
    },
  });

  // Mutation to disable MFA
  const disableMutation = useMutation({
    mutationFn: async (token: string) => {
      const res = await apiRequest("POST", "/api/mfa/disable", { token });
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || "Failed to disable MFA");
      }
      return res.json();
    },
    onSuccess: () => {
      setShowDisableForm(false);
      disableForm.reset();
      queryClient.invalidateQueries({ queryKey: ["/api/mfa/status"] });
      toast({
        title: "MFA Disabled",
        description: "Two-factor authentication has been disabled for your account.",
        variant: "default",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Mutation to generate new backup codes
  const backupCodesMutation = useMutation({
    mutationFn: async (token: string) => {
      const res = await apiRequest("POST", "/api/mfa/backup-codes", { token });
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || "Failed to generate backup codes");
      }
      return res.json();
    },
    onSuccess: (data) => {
      setBackupCodes(data.backupCodes);
      setShowNewBackupCodes(true);
      backupCodesForm.reset();
      toast({
        title: "Backup Codes Generated",
        description: "New backup codes have been generated. Be sure to save them securely.",
        variant: "default",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Handle disable form submission
  const onDisableSubmit = (values: TokenFormValues) => {
    disableMutation.mutate(values.token);
  };

  // Handle backup codes form submission
  const onBackupCodesSubmit = (values: TokenFormValues) => {
    backupCodesMutation.mutate(values.token);
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Security Settings</CardTitle>
          <CardDescription>Loading your security settings...</CardDescription>
        </CardHeader>
        <CardContent className="flex justify-center p-6">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  if (!mfaStatus?.enabled) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Two-Factor Authentication</CardTitle>
          <CardDescription>
            Enhance your account security by enabling two-factor authentication.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Alert className="mb-4 border-yellow-500 bg-yellow-50 dark:bg-yellow-950/50">
            <AlertCircle className="h-4 w-4 text-yellow-600 dark:text-yellow-500" />
            <AlertTitle>Your account is not fully protected</AlertTitle>
            <AlertDescription>
              We recommend enabling two-factor authentication for additional security.
            </AlertDescription>
          </Alert>
          <div className="text-sm space-y-2">
            <p>Two-factor authentication adds an extra layer of security to your account by requiring:</p>
            <ol className="list-decimal ml-5 space-y-1">
              <li>Your password</li>
              <li>A verification code from your authenticator app</li>
            </ol>
          </div>
        </CardContent>
        <CardFooter>
          <Button className="w-full" onClick={() => onSetupMFA?.()}>
            <Shield className="w-4 h-4 mr-2" />
            Set Up Two-Factor Authentication
          </Button>
        </CardFooter>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Two-Factor Authentication</CardTitle>
        <CardDescription>
          Manage your two-factor authentication settings.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {!showDisableForm && !showNewBackupCodes && (
          <>
            <Alert className="border-green-500">
              <CheckCircle2 className="h-4 w-4 text-green-500" />
              <AlertTitle>Two-factor authentication is enabled</AlertTitle>
              <AlertDescription>
                Your account is protected with two-factor authentication.
                {mfaStatus.enabledAt && (
                  <span className="block text-xs mt-1">
                    Enabled on {format(new Date(mfaStatus.enabledAt), "MMM d, yyyy")}
                  </span>
                )}
              </AlertDescription>
            </Alert>

            <div className="space-y-4 pt-2">
              <div className="pt-2 border-t">
                <h3 className="text-sm font-medium text-destructive mb-1">Disable Two-Factor Authentication</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                  This will remove the additional security layer from your account. Not recommended.
                </p>
                <Button 
                  variant="destructive" 
                  onClick={() => {
                    setShowDisableForm(true);
                    setShowNewBackupCodes(false);
                    disableForm.reset();
                  }}
                >
                  Disable Two-Factor Authentication
                </Button>
              </div>
            </div>
          </>
        )}

        {showDisableForm && (
          <div>
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Warning: Reduced Security</AlertTitle>
              <AlertDescription>
                Disabling two-factor authentication will make your account less secure.
              </AlertDescription>
            </Alert>
            
            <Form {...disableForm}>
              <form onSubmit={disableForm.handleSubmit(onDisableSubmit)} className="space-y-4">
                <FormField
                  control={disableForm.control}
                  name="token"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Verification Code</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Enter the 6-digit code"
                          {...field}
                          autoComplete="off"
                        />
                      </FormControl>
                      <FormDescription>
                        Enter the verification code from your authenticator app to confirm
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="flex space-x-2">
                  <Button
                    type="submit"
                    variant="destructive"
                    disabled={disableMutation.isPending}
                  >
                    {disableMutation.isPending && (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    )}
                    Disable Two-Factor Authentication
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowDisableForm(false)}
                    disabled={disableMutation.isPending}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </Form>
          </div>
        )}

        {!showDisableForm && !showNewBackupCodes && (
          <Form {...backupCodesForm}>
            <form onSubmit={backupCodesForm.handleSubmit(onBackupCodesSubmit)} className="space-y-4">
              <FormField
                control={backupCodesForm.control}
                name="token"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Verification Code</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter the 6-digit code"
                        {...field}
                        autoComplete="off"
                      />
                    </FormControl>
                    <FormDescription>
                      Enter the verification code from your authenticator app to get new backup codes
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="flex space-x-2">
                <Button
                  type="submit"
                  disabled={backupCodesMutation.isPending}
                >
                  {backupCodesMutation.isPending && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  Generate New Backup Codes
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => backupCodesForm.reset()}
                  disabled={backupCodesMutation.isPending}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </Form>
        )}

        {showNewBackupCodes && (
          <div className="space-y-4">
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Important</AlertTitle>
              <AlertDescription>
                These new backup codes replace any previous ones. Save them in a secure location.
              </AlertDescription>
            </Alert>
            <div className="grid grid-cols-2 gap-2">
              {backupCodes.map((code, index) => (
                <code key={index} className="p-2 bg-gray-100 dark:bg-gray-800 rounded-md text-center font-mono">
                  {code}
                </code>
              ))}
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Each code can only be used once. Store these somewhere safe, like a password manager.
            </p>
            <Button 
              onClick={() => setShowNewBackupCodes(false)} 
              className="w-full"
            >
              I've Saved My Backup Codes
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}